/**
 * Pipeline Labs Person Filters Component
 *
 * Handles person-related filters with camelCase field names for Pipeline Labs API
 * Uses interactive components: TagInput, MultiSelectGrid, AutocompleteInput
 */

import React from 'react';
import TagInput from '../inputs/TagInput';
import MultiSelectGrid from '../inputs/MultiSelectGrid';
import AutocompleteInput from '../inputs/AutocompleteInput';
import { SENIORITY_LEVELS, JOB_FUNCTIONS, JOB_TITLES } from '../../../constants/pipelineLabsOptions';
import { COUNTRIES, REGIONS } from '../../../constants/autocompleteData';

const PipelineLabsPersonFilters = ({ filters, setFilters }) => {
  const handleArrayChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleBooleanChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      {/* Job Titles - AutocompleteInput with 500 predefined options (strict mode - no custom values) */}
      <AutocompleteInput
        label="Job Titles (Include)"
        value={filters.personTitleIncludes || []}
        onChange={(value) => handleArrayChange('personTitleIncludes', value)}
        placeholder="Type to search job titles..."
        suggestions={JOB_TITLES}
        maxTags={100}
        hint="Search and select from 500+ job titles"
        strictMode={true}
      />

      <AutocompleteInput
        label="Job Titles (Exclude)"
        value={filters.personTitleExcludes || []}
        onChange={(value) => handleArrayChange('personTitleExcludes', value)}
        placeholder="Type to search job titles..."
        suggestions={JOB_TITLES}
        maxTags={50}
        hint="Exclude specific job titles"
        strictMode={true}
      />

      {/* Include Similar Titles - Checkbox */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none'
        }}>
          <input
            type="checkbox"
            id="includeSimilarTitles"
            checked={filters.includeSimilarTitles || false}
            onChange={(e) => handleBooleanChange('includeSimilarTitles', e.target.checked)}
            style={{
              width: '16px',
              height: '16px',
              cursor: 'pointer',
              accentColor: 'var(--color-info-hover)',
              marginRight: '8px'
            }}
          />
          <span style={{
            fontSize: '13px',
            color: 'var(--color-text-secondary)',
            fontWeight: '500'
          }}>
            Include similar title variants and aliases
          </span>
        </label>
        <p style={{
          fontSize: '12px',
          color: 'var(--color-text-muted)',
          marginTop: '4px',
          marginLeft: '24px'
        }}>
          Automatically expands search to related job titles
        </p>
      </div>

      {/* Additional Custom Titles - TagInput for niche titles */}
      <TagInput
        label="Additional Custom Titles"
        value={filters.personTitleExtraIncludes || []}
        onChange={(value) => handleArrayChange('personTitleExtraIncludes', value)}
        placeholder="e.g., Ninja, Rockstar (Press Enter to add)"
        maxTags={50}
        hint="Add niche titles not in the predefined list"
      />

      {/* Seniority - MultiSelectGrid for visual selection */}
      <MultiSelectGrid
        label="Seniority Levels (Include)"
        options={SENIORITY_LEVELS}
        value={filters.seniorityIncludes || []}
        onChange={(value) => handleArrayChange('seniorityIncludes', value)}
        minWidth="120px"
        hint="Select one or more seniority levels"
      />

      <MultiSelectGrid
        label="Seniority Levels (Exclude)"
        options={SENIORITY_LEVELS}
        value={filters.seniorityExcludes || []}
        onChange={(value) => handleArrayChange('seniorityExcludes', value)}
        minWidth="120px"
        hint="Exclude specific seniority levels"
      />

      {/* Job Functions - MultiSelectGrid */}
      <MultiSelectGrid
        label="Job Functions (Include)"
        options={JOB_FUNCTIONS}
        value={filters.personFunctionIncludes || []}
        onChange={(value) => handleArrayChange('personFunctionIncludes', value)}
        minWidth="160px"
        hint="Select one or more functional departments"
      />

      <MultiSelectGrid
        label="Job Functions (Exclude)"
        options={JOB_FUNCTIONS}
        value={filters.personFunctionExcludes || []}
        onChange={(value) => handleArrayChange('personFunctionExcludes', value)}
        minWidth="160px"
        hint="Exclude specific functions"
      />

      {/* Person Location - Countries - AutocompleteInput for 237 countries (strict mode) */}
      <AutocompleteInput
        label="Person Countries (Include)"
        value={filters.personLocationCountryIncludes || []}
        onChange={(value) => handleArrayChange('personLocationCountryIncludes', value)}
        placeholder="Type to search countries..."
        suggestions={COUNTRIES}
        hint="Person's location - country (237 countries available)"
        strictMode={true}
      />

      <AutocompleteInput
        label="Person Countries (Exclude)"
        value={filters.personLocationCountryExcludes || []}
        onChange={(value) => handleArrayChange('personLocationCountryExcludes', value)}
        placeholder="Type to search countries..."
        suggestions={COUNTRIES}
        hint="Exclude specific countries"
        strictMode={true}
      />

      {/* Person Location - States - AutocompleteInput for 494 states/regions (strict mode) */}
      <AutocompleteInput
        label="Person States/Regions (Include)"
        value={filters.personLocationStateIncludes || []}
        onChange={(value) => handleArrayChange('personLocationStateIncludes', value)}
        placeholder="Type to search states/regions..."
        suggestions={REGIONS}
        hint="Person's location - state/region (494 options available)"
        strictMode={true}
      />

      <AutocompleteInput
        label="Person States/Regions (Exclude)"
        value={filters.personLocationStateExcludes || []}
        onChange={(value) => handleArrayChange('personLocationStateExcludes', value)}
        placeholder="Type to search states/regions..."
        suggestions={REGIONS}
        hint="Exclude specific states/regions"
        strictMode={true}
      />

      {/* Person Location - Cities - TagInput for free-text */}
      <TagInput
        label="Person Cities (Include)"
        value={filters.personLocationCityIncludes || []}
        onChange={(value) => handleArrayChange('personLocationCityIncludes', value)}
        placeholder="e.g., San Francisco, New York (Press Enter to add)"
        hint="Add specific cities"
      />

      <TagInput
        label="Person Cities (Exclude)"
        value={filters.personLocationCityExcludes || []}
        onChange={(value) => handleArrayChange('personLocationCityExcludes', value)}
        placeholder="e.g., Remote locations (Press Enter to add)"
        hint="Exclude specific cities"
      />
    </>
  );
};

export default PipelineLabsPersonFilters;
